import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { Controller, useWatch, type Control } from 'react-hook-form';

import EventFormField from '@/domain/events/components/EventFormField';
import { type EventFormValues } from '@/domain/events/types';
import { RECURRENCE_FREQUENCIES, WEEKDAYS, weekdayShortLabel } from '@/domain/events/utils';
import { useTranslate } from '@/state/i18n';

interface Props {
  control: Control<EventFormValues>;
  /** Earliest start date for a new event (YYYY-MM-DD); editing has no minimum. */
  minDate?: string;
}

/** P09 "Quando": date, time and the recurrence (daily, weekly on chosen days, monthly, until). */
const EventWhenFields = ({ control, minDate }: Props) => {
  const t = useTranslate('domain.events.components.EventWhenFields');
  const recurrenceFreq = useWatch({ control, name: 'recurrenceFreq' });

  return (
    <Stack gap={2.5}>
      <Box sx={sx.row}>
        <EventFormField
          control={control}
          name="startDate"
          label={t('startDate')}
          type="date"
          slotProps={{ htmlInput: { min: minDate } }}
        />
        <EventFormField
          control={control}
          name="startTime"
          label={t('startTime')}
          optionalLabel={t('optional')}
          type="time"
        />
      </Box>
      <Controller
        name="recurrenceFreq"
        control={control}
        render={({ field }) => (
          <FormControl fullWidth>
            <FormLabel htmlFor="recurrenceFreq">{t('recurrence')}</FormLabel>
            <TextField
              {...field}
              id="recurrenceFreq"
              select
              fullWidth
              helperText={t('recurrenceHint')}
              slotProps={{ select: { displayEmpty: true } }}
            >
              <MenuItem value="">{t('noRepeat')}</MenuItem>
              {RECURRENCE_FREQUENCIES.map((frequency) => (
                <MenuItem key={frequency} value={frequency}>
                  {t(`frequency.${frequency}`)}
                </MenuItem>
              ))}
            </TextField>
          </FormControl>
        )}
      />
      {recurrenceFreq === 'weekly' ? (
        <Controller
          name="recurrenceWeekdays"
          control={control}
          render={({ field }) => (
            <FormControl fullWidth>
              <FormLabel id="recurrenceWeekdays-label">{t('weekdays')}</FormLabel>
              <ToggleButtonGroup
                aria-labelledby="recurrenceWeekdays-label"
                value={field.value}
                onChange={(_, value: number[]) => field.onChange(value)}
                size="small"
                sx={sx.weekdays}
              >
                {WEEKDAYS.map((weekday) => (
                  <ToggleButton key={weekday} value={weekday}>
                    {weekdayShortLabel(weekday)}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              <FormHelperText>{t('weekdaysHint')}</FormHelperText>
            </FormControl>
          )}
        />
      ) : null}
      {recurrenceFreq ? (
        <EventFormField
          control={control}
          name="recurrenceUntil"
          label={t('until')}
          optionalLabel={t('optional')}
          type="date"
          hint={t('untilHint')}
        />
      ) : null}
    </Stack>
  );
};

export default EventWhenFields;

const sx = {
  row: { display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } },
  weekdays: { flexWrap: 'wrap', gap: 0.5 },
};
